import { Alert, Card, Collapse, Descriptions, Flex, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  ConversationCompletionDetail,
  ConversationOverviewFlag,
  VoiceCallDetail
} from '@/api/chat/interfaces';
import { CompletionDetails } from './CompletionDetails';
import { CopyableId } from '@/components/shared/CopyableId';

const { Text } = Typography;

type ConversationDetailSidebarProps = {
  flag?: ConversationOverviewFlag;
  completion: ConversationCompletionDetail | null;
  threadId: string;
  tenantName: string | null;
  flowId: string | null;
  callId: string | null;
  sessionIds: string[];
  voiceCall: VoiceCallDetail | null | undefined;
  voiceLoading?: boolean;
  contextSummary: string | null;
  voiceContext: string | null;
};

export function ConversationDetailSidebar({
  flag,
  completion,
  threadId,
  tenantName,
  flowId,
  callId,
  sessionIds,
  voiceCall,
  voiceLoading,
  contextSummary,
  voiceContext
}: ConversationDetailSidebarProps) {
  const { t } = useTranslation('admin');

  return (
    <Flex vertical gap={16}>
      {flag === 'RED' ? (
        <Alert type="error" showIcon title={t('conversationOverview.detail.redFlagAlert')} />
      ) : null}
      {completion?.technicalIssue ? (
        <Alert
          type="warning"
          showIcon
          title={t('conversationOverview.detail.technicalIssueAlert')}
        />
      ) : null}
      <Card title={t('conversationOverview.detail.completion')} size="small">
        {completion ? (
          <CompletionDetails completion={completion} />
        ) : (
          <Text type="secondary">{t('conversationOverview.detail.noData')}</Text>
        )}
      </Card>
      <Card title={t('conversationOverview.detail.identifiers')} size="small">
        <Descriptions
          column={1}
          size="small"
          items={[
            {
              key: 'thread',
              label: t('conversationOverview.detail.threadId'),
              children: <CopyableId value={threadId} />
            },
            {
              key: 'client',
              label: t('conversationOverview.detail.client'),
              children: tenantName ?? '—'
            },
            {
              key: 'flow',
              label: t('conversationOverview.detail.flowId'),
              children: flowId ? <CopyableId value={flowId} /> : '—'
            },
            {
              key: 'call',
              label: t('conversationOverview.detail.callId'),
              children: callId ? <CopyableId value={callId} /> : '—'
            },
            {
              key: 'session',
              label: t('conversationOverview.detail.sessionId'),
              children: sessionIds.length
                ? sessionIds.map((id) => <CopyableId key={id} value={id} />)
                : '—'
            }
          ]}
        />
      </Card>
      {callId ? (
        <Card
          title={t('conversationOverview.detail.voice')}
          size="small"
          loading={voiceLoading}
        >
          {voiceCall ? (
            <Descriptions
              column={1}
              size="small"
              items={[
                {
                  key: 'from',
                  label: t('conversationOverview.detail.fromNumber'),
                  children: voiceCall.fromNumber || '—'
                },
                {
                  key: 'to',
                  label: t('conversationOverview.detail.toNumber'),
                  children: voiceCall.toNumber || '—'
                },
                {
                  key: 'status',
                  label: t('conversationOverview.detail.callStatus'),
                  children: voiceCall.status
                },
                {
                  key: 'reason',
                  label: t('conversationOverview.detail.endReason'),
                  children: voiceCall.endReason || '—'
                },
                {
                  key: 'code',
                  label: t('conversationOverview.detail.endCode'),
                  children: voiceCall.endCode ?? '—'
                },
                {
                  key: 'provider',
                  label: t('conversationOverview.detail.provider'),
                  children: voiceCall.provider || '—'
                },
                {
                  key: 'engine',
                  label: t('conversationOverview.detail.engine'),
                  children: voiceCall.engine || '—'
                },
                {
                  key: 'transport',
                  label: t('conversationOverview.detail.transport'),
                  children: voiceCall.transport || '—'
                }
              ]}
            />
          ) : (
            <Text type="secondary">{t('conversationOverview.detail.noData')}</Text>
          )}
        </Card>
      ) : null}
      {contextSummary || voiceContext ? (
        <Collapse
          items={[
            ...(contextSummary
              ? [
                  {
                    key: 'context',
                    label: t('conversationOverview.detail.contextSummary'),
                    children: (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{contextSummary}</pre>
                    )
                  }
                ]
              : []),
            ...(voiceContext
              ? [
                  {
                    key: 'voice',
                    label: t('conversationOverview.detail.voiceContext'),
                    children: (
                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{voiceContext}</pre>
                    )
                  }
                ]
              : [])
          ]}
        />
      ) : null}
    </Flex>
  );
}
